/**
 * IntegrityHero.tsx — the App Integrity page's answer-first banner (UI v2,
 * 2026-07-31: "the UI is extremely confusing" → the page now OPENS with the
 * one thing an auditor wants to know: is the calculation pipeline intact?).
 *
 * Shows the latest suite verdict as a full-width status band with the audit
 * project cross-link, when/who/what of the last run, the next scheduled run,
 * and the two primary actions (Run all · latest Formula Verification PDF).
 */
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline,
  ErrorOutline,
  HelpOutline,
  LaunchOutlined,
  PictureAsPdf,
  PlayArrow,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import type { IntegrityRun, IntegrityScheduleSettings } from '../../../services/appIntegrityService';

interface IntegrityHeroProps {
  latestRun: IntegrityRun | null;
  totalChecks: number;
  activeFixtureCount: number;
  /** Audit project reference parsed from fixture provenance, e.g.
   *  {id: 'PRJ-App-Inegrity', name: 'App Integrity Check Data'}. */
  auditProject: { id: string; name: string } | null;
  schedule: IntegrityScheduleSettings | null;
  running: boolean;
  downloading: boolean;
  onRunAll: () => void;
  onDownloadPdf: () => void;
}

export const IntegrityHero = ({
  latestRun,
  totalChecks,
  activeFixtureCount,
  auditProject,
  schedule,
  running,
  downloading,
  onRunAll,
  onDownloadPdf,
}: IntegrityHeroProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const verdict: 'passed' | 'failed' | 'running' | 'none' = running
    ? 'running'
    : !latestRun
      ? 'none'
      : latestRun.Status === 'passed'
        ? 'passed'
        : 'failed';

  const tone =
    verdict === 'passed' ? theme.palette.success.main
    : verdict === 'failed' ? theme.palette.error.main
    : theme.palette.grey[600];

  const headline =
    verdict === 'running' ? 'SUITE RUNNING…'
    : verdict === 'none' ? 'NO RUNS YET'
    : verdict === 'passed' ? 'ALL GOLDEN CHECKS PASSED'
    : `${latestRun!.FailCount + latestRun!.ErrorCount} OF ${latestRun!.FixtureCount} GOLDEN TESTS FAILED`;

  const Icon =
    verdict === 'passed' ? CheckCircleOutline
    : verdict === 'failed' ? ErrorOutline
    : HelpOutline;

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: 1,
        borderColor: alpha(tone, 0.4),
        backgroundColor: alpha(tone, 0.06),
        px: 2.5,
        py: 2,
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      {verdict === 'running'
        ? <CircularProgress size={34} />
        : <Icon sx={{ fontSize: 42, color: tone }} />}
      <Box sx={{ minWidth: 220, flex: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, color: tone }}>
          {headline}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {latestRun
            ? `${latestRun.PassCount}/${latestRun.FixtureCount} golden tests · ${totalChecks} value checks · ` +
              `last run ${new Date(latestRun.StartedAt).toLocaleString()} · ${latestRun.TriggeredBy} · app ${latestRun.AppVersion}`
            : `${activeFixtureCount} golden tests ready — press Run all to execute the first suite.`}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
          {auditProject && (
            <Tooltip title="All golden values live as real tests in this project — open it to cross-check any value">
              <Chip
                size="small"
                clickable
                icon={<LaunchOutlined />}
                label={`Audit project: ${auditProject.id} — ${auditProject.name}`}
                onClick={() => navigate(`/projects/${auditProject.id}/workorders`)}
                sx={{ fontWeight: 600 }}
                variant="outlined"
                color="primary"
              />
            </Tooltip>
          )}
          {schedule?.enabled && schedule.nextDueAt && (
            <Typography variant="caption" color="text.secondary">
              Next scheduled run: {new Date(schedule.nextDueAt).toLocaleString()}
              {schedule.recipients.length > 0 ? ` · alerts → ${schedule.recipients.join(', ')}` : ' · no alert recipients set'}
            </Typography>
          )}
          {schedule && !schedule.enabled && (
            <Typography variant="caption" color="text.secondary">
              Scheduled runs are off — configure them under Automation.
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {latestRun && (
          <Button
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={14} /> : <PictureAsPdf />}
            disabled={downloading}
            onClick={onDownloadPdf}
          >
            Formula PDF
          </Button>
        )}
        <Button
          variant="contained"
          startIcon={running ? <CircularProgress size={14} color="inherit" /> : <PlayArrow />}
          disabled={running || activeFixtureCount === 0}
          onClick={onRunAll}
        >
          {running ? 'Running…' : 'Run all'}
        </Button>
      </Box>
    </Box>
  );
};
